import { Form, Input, Button, message } from "antd";
import { useState } from "react";
import mockup from "../../../../public/images/login-mockup.png";
import "./style.scss";
import { Logo } from "../../../icons";
import Modal from "../../../components/modal";
import successicon from "../../../../public/images/success-tick.svg";
import {
  useGenerateOtp,
  useVerifyOtp,
  useResetPassword,
} from "../../../hooks/useOtp";
import { useNavigate } from "react-router";

// Helper to mask email or phone for display
const formatIdentifierForDisplay = (identifier?: string) => {
  if (!identifier) return "your registered contact";

  // If it's an email
  if (identifier.includes("@")) {
    const [name, domain] = identifier.split("@");
    return `${name[0]}***@${domain}`;
  }

  // If it's a phone number
  if (/^\d+$/.test(identifier)) {
    return `${identifier.slice(0, 2)}***${identifier.slice(-2)}`;
  }

  return identifier;
};

const SignUp = () => {
  const [screen, setScreen] = useState<"register" | "otp" | "createPassword">(
    "register"
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState("");
  const [otpVerificationId, setOtpVerificationId] = useState<number | null>(
    null
  );
  const [otpValues, setOtpValues] = useState<string[]>(["", "", "", "", ""]);
  const [userId, setUserId] = useState<string | null>(null);

  const [form] = Form.useForm();
  const { mutate: generateOtp, isPending: sendingOtp } = useGenerateOtp();
  const { mutate: verifyOtp, isPending: verifyingOtp } = useVerifyOtp();
  const { mutate: resetPassword, isPending: resettingPassword } =
    useResetPassword();
  const navigate = useNavigate();
  // Step 1: Generate OTP
  const onFinish = (values: any) => {
    generateOtp(
      { receiver_id: values.username, purpose: "sign up", country_code: "27" },
      {
        onSuccess: (res: any) => {
          setUserId(res.data.user_id);
          setOtpVerificationId(res.data.otp_verification_id);
          setScreen("otp");
          setOtpValues(["", "", "", "", ""]);
        },
        onError: (err: any) => {
          message.error(err?.response?.data?.message || "Failed to send OTP");
          console.error("OTP generation failed:", err);
        },
      }
    );
  };

  // Step 2: Verify OTP
  const handleOtpSubmit = (otp: string) => {
    if (!otpVerificationId || otp.length !== 5) return;
    const numericOtp = Number(otp);
    if (isNaN(numericOtp)) return console.error("Invalid OTP");

    verifyOtp(
      { otp_verification_id: otpVerificationId, otp: numericOtp },
      {
        onSuccess: () => {
          message.success("OTP verified successfully"),
            setScreen("createPassword");
        },
        onError: (err: any) => {
          message.error(
            err?.response?.data?.message || "OTP verification failed"
          ),
            console.error("OTP verification failed:", err);
        },
      }
    );
  };

  // Step 3: Create/Reset Password
  const handlePasswordSubmit = (values: any) => {
    if (!userId) {
      message.error("Invalid session. Please try again.");
      return;
    }

    resetPassword(
      { user_id: userId, password: values.password },
      {
        onSuccess: () => {
          message.success("Password created successfully"),
            setIsModalOpen(true);
        },
        onError: (err: any) => {
          const errorMessage =
            err?.response?.data?.message || "Failed to create password";
          message.error(errorMessage);
        },
      }
    );
  };

  function evaluatePasswordStrength(password: any) {
    let count = 0;

    if (/\d/.test(password)) count++; // number
    if (/[A-Z]/.test(password)) count++; // uppercase
    if (/[a-z]/.test(password)) count++; // lowercase
    if (password.length >= 8 && password.length <= 30) count++; // length

    if (count === 4) return { label: "Strong", level: 4 };
    if (count >= 2) return { label: "Fair", level: count };
    return { label: "Weak", level: count };
  }

  const strengthMap: Record<"Weak" | "Fair" | "Strong", number> = {
    Weak: 1,
    Fair: 2,
    Strong: 3,
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { label } = evaluatePasswordStrength(e.target.value);
    setPasswordStrength(label);
  };

  const handleOk = () => {
    setIsModalOpen(false);
    form.resetFields();
    navigate("/login");
    setOtpValues(["", "", "", "", ""]);
  };
  const handleCancel = () => setIsModalOpen(false);

  // OTP input logic (auto-focus + backspace deletion)
  const handleOtpChange = (index: number, value: string) => {
    const newOtp = [...otpValues];
    newOtp[index] = value;
    setOtpValues(newOtp);

    if (value && index < 4) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      nextInput?.focus();
    }

    const otpString = newOtp.join("");
    if (otpString.length === 5 && !newOtp.includes("")) {
      handleOtpSubmit(otpString);
    }
  };

  const handleOtpKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === "Backspace" && !otpValues[index] && index > 0) {
      const prevInput = document.getElementById(
        `otp-${index - 1}`
      ) as HTMLInputElement;
      const newOtp = [...otpValues];
      newOtp[index - 1] = "";
      setOtpValues(newOtp);
      prevInput?.focus();
    }
  };

  const handleResendOtp = () => {
    const username = form.getFieldValue("username");
    if (!username) return;
    generateOtp(
      { receiver_id: username, purpose: "sign up", country_code: "27" },
      {
        onSuccess: (res) => {
          setUserId(res.data?.user_id);

          setOtpVerificationId(res.data.otp_verification_id);
          setOtpValues(["", "", "", "", ""]);
          message.success("OTP resent successfully");
        },
        onError: (err: any) => message.error("Resend OTP failed:", err),
      }
    );
  };

  return (
    <div className="register-page">
      <div className="register-info-panel">
        <div className="info-header">
          <Logo width={142} height={32} />
          <div className="info-content">
            <h1>Join Us Today!</h1>
            <p className="subtitle">Sign up and start using the platform</p>
          </div>
        </div>
        <div className="info-card-placeholder">
          <img src={mockup} alt="App preview" className="card-image" />
        </div>
      </div>

      <div className="register-form-panel">
        <div className="form-container transition-all duration-300">
          {/* Register Screen */}
          {screen === "register" && (
            <>
              <h2>Create your account</h2>
              <p className="form-subtitle">
                Sign up with your email ID or mobile number
              </p>
              <Form
                name="register_form"
                className="register-form"
                onFinish={onFinish}
                form={form}
              >
                <Form.Item
                  name="username"
                  rules={[
                    {
                      required: true,
                      message: "Please input your Email or Mobile!",
                    },
                  ]}
                >
                  <Input
                    placeholder="Email/Mobile number"
                    className="register-input"
                  />
                </Form.Item>
                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    className="register-form-button"
                    loading={sendingOtp}
                  >
                    Next
                  </Button>
                </Form.Item>
              </Form>
            </>
          )}

          {/* OTP Screen */}
          {screen === "otp" && (
            <>
              <h2>OTP Verification</h2>
              <p className="form-subtitle ">
                A four digit PIN has been sent to{" "}
                {formatIdentifierForDisplay(form.getFieldValue("username"))}{" "}
                <a onClick={() => setScreen("register")}>Change</a>
              </p>
              <div className="otp-section">
                <div className="otp-inputs">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Input
                      key={i}
                      id={`otp-${i}`}
                      maxLength={1}
                      className="otp-box"
                      value={otpValues[i]}
                      onChange={(e) => handleOtpChange(i, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(i, e)}
                    />
                  ))}
                </div>
                <p className="support-text">
                  Haven’t received an OTP yet?{" "}
                  <a onClick={handleResendOtp}>Resend</a>
                  <br />
                  Need help? <a href="#">Contact support</a>
                </p>
              </div>
              <div style={{ marginTop: 16 }}>
                <Button
                  type="primary"
                  className="register-form-button"
                  onClick={() => handleOtpSubmit(otpValues.join(""))}
                  loading={verifyingOtp}
                >
                  Next
                </Button>
              </div>
            </>
          )}

          {/* Create Password Screen */}
          {screen === "createPassword" && (
            <div className="create-password-section">
              <h2>Create a Password</h2>
              <p className="form-subtitle">
                Set a password to secure your account
              </p>
              <Form
                form={form}
                className="register-form"
                onFinish={handlePasswordSubmit}
                layout="vertical"
              >
                <Form.Item
                  name="password"
                  rules={[
                    { required: true, message: "Enter new password" },
                    {
                      pattern: /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)[^\s]{8,30}$/,
                      message:
                        "Password must be 8–30 characters long, contain uppercase, lowercase, and a digit, and have no spaces.",
                    },
                  ]}
                >
                  <Input.Password
                    maxLength={30}
                    placeholder="New password"
                    onChange={handlePasswordChange}
                  />
                </Form.Item>

                <Form.Item
                  name="confirmPassword"
                  dependencies={["password"]}
                  validateTrigger={["onChange", "onBlur"]}
                  rules={[
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const password = getFieldValue("password");

                        if (!value) {
                          return Promise.reject(
                            new Error("Confirm your password")
                          );
                        }

                        if (value !== password) {
                          return Promise.reject(
                            new Error("Passwords do not match!")
                          );
                        }

                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <Input.Password placeholder="Confirm password" />
                </Form.Item>

                {form.getFieldValue("password")?.length > 0 && (
                  <div className="strength-visualizer">
                    <div className="strength-bars">
                      {[0, 1, 2].map((i) => (
                        <div
                          key={i}
                          className={`bar ${
                            i <
                            strengthMap[
                              passwordStrength as "Weak" | "Fair" | "Strong"
                            ]
                              ? passwordStrength.toLowerCase()
                              : ""
                          }`}
                        />
                      ))}
                    </div>
                    <span className="strength-label">{passwordStrength}</span>
                  </div>
                )}

                <div className="password-helper-area">
                  <p className="mb-0">Your password must:</p>
                  <ul>
                    <li>
                      Contain at least one number, upper and lower case letter
                    </li>
                    <li>Be between 8 and 30 characters in length</li>
                    <li>Try to keep your user ID out of the password</li>
                  </ul>
                </div>

                <Button
                  type="primary"
                  htmlType="submit"
                  className="register-form-button"
                  loading={resettingPassword}
                >
                  Submit
                </Button>
                <Modal
                  title=""
                  closable={{ "aria-label": "Custom Close Button" }}
                  open={isModalOpen}
                  onOk={handleOk}
                  onCancel={handleOk}
                  className="confirm-modal"
                >
                  <div className="success-pic">
                    <img src={successicon} />
                  </div>
                  <h1>Account Created!</h1>
                  <p>Your account has been successfully created.</p>
                  <Button
                    type="primary"
                    className="login-form-button"
                    onClick={handleOk}
                  >
                    OK
                  </Button>
                </Modal>
              </Form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SignUp;
